import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Edit, Power, PowerOff, Eye, DollarSign, Lock, Settings, Mail, Key, Trash2,
  CheckCircle, XCircle, Send, UserPlus, KeyRound, LogIn, Loader2, RefreshCw,
} from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Checkbox } from '@/components/ui/checkbox'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { DataTable, DataTableAction, DataTableBulkAction, DataTableColumn } from '@/components/admin/DataTable'
import { ExportCsvButton, CsvColumn } from '@/components/admin/ExportCsvButton'
import { apiClient } from '@/lib/api'
import { useToast } from '@/hooks/use-toast'
import { useAuth } from '@/contexts/AuthContext'

// Admin Panel Redesign — self-contained Users section. Ported every working
// dialog/action from the old AdminPanel.tsx "users" tab (Edit, Assign Credits,
// View Logs, Permissions, Module Access, Send Email, Password Reset, Create,
// Invite, Approve/Reject, Activate/Deactivate) onto the new DataTable, and
// added multi-select bulk ops, API key management, impersonation ("View as"),
// CSV export, and tiers-list-driven defaults. Fixes two real bugs from the
// old file: window.confirm()/window.prompt() for destructive actions (now
// real AlertDialogs) and three separate hardcoded tier->limit/tier->modules
// maps that could drift (now a single apiClient.getTiersList() call).

interface AdminUser {
  id: string
  email: string
  first_name: string
  last_name: string
  role: string
  subscription_tier: string
  request_limit: number
  requests_used: number
  is_active: boolean
  created_at: string
  last_login?: string
  modules?: string[]
  registration_ip?: string
  invite_sent_at?: string
  invite_sent_by?: string
  account_activated_at?: string
  email_verified?: boolean
  admin_approved?: boolean
  organization_id?: string
  permissions?: string[]
}

interface Organization {
  id: string
  name: string
  [key: string]: any
}

interface ModuleInfo {
  id: string
  name: string
  description: string
}

interface ApiKeyDetail {
  masked_key: string
  label: string | null
  created_at: string | null
  last_used: string | null
}

type TiersList = Record<string, { request_limit: number; modules: string[] }>

const AVAILABLE_PERMISSIONS = ['read', 'write', 'admin', 'export', 'manage_users', 'manage_credits', 'view_logs', 'system_settings']
const FALLBACK_TIER_KEYS = ['freemium', 'starter', 'professional', 'enterprise']

function tierLabel(tier: string, tiers: TiersList) {
  const limit = tiers[tier]?.request_limit
  const label = tier.charAt(0).toUpperCase() + tier.slice(1)
  if (limit === undefined) return label
  return `${label} (${limit === -1 ? 'Unlimited' : limit})`
}

export default function UsersSection() {
  const { user: currentUser, startImpersonation } = useAuth()
  const { toast } = useToast()
  const navigate = useNavigate()

  const [users, setUsers] = useState<AdminUser[]>([])
  const [organizations, setOrganizations] = useState<Organization[]>([])
  const [tiersList, setTiersList] = useState<TiersList>({})
  const [availableModules, setAvailableModules] = useState<ModuleInfo[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'stale_approved' | 'active'>('all')
  const [sortBy, setSortBy] = useState<'newest' | 'oldest' | 'last_activity'>('newest')
  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null)

  // Create / Invite
  const [showCreateDialog, setShowCreateDialog] = useState(false)
  const [newUser, setNewUser] = useState({
    email: '', password: '', first_name: '', last_name: '',
    role: 'user', subscription_tier: 'freemium', organization_id: '',
  })
  const [creatingUser, setCreatingUser] = useState(false)

  const [showInviteDialog, setShowInviteDialog] = useState(false)
  const [inviteUser, setInviteUser] = useState({
    email: '', first_name: '', last_name: '', role: 'user', subscription_tier: 'freemium',
  })
  const [invitingUser, setInvitingUser] = useState(false)

  // Edit
  const [showEditDialog, setShowEditDialog] = useState(false)
  const [editUser, setEditUser] = useState<Partial<AdminUser>>({})
  const [savingEdit, setSavingEdit] = useState(false)

  // Credits
  const [showCreditDialog, setShowCreditDialog] = useState(false)
  const [creditData, setCreditData] = useState({ user_id: '', credits: 0, reason: '' })
  const [assigningCredits, setAssigningCredits] = useState(false)
  const [resetCreditsTarget, setResetCreditsTarget] = useState<AdminUser | null>(null)

  // Logs
  const [showLogsDialog, setShowLogsDialog] = useState(false)
  const [queryLogs, setQueryLogs] = useState<any[]>([])
  const [logsLoading, setLogsLoading] = useState(false)

  // Permissions
  const [showPermissionsDialog, setShowPermissionsDialog] = useState(false)
  const [userPermissions, setUserPermissions] = useState<string[]>([])
  const [savingPermissions, setSavingPermissions] = useState(false)

  // Module access
  const [showModulesDialog, setShowModulesDialog] = useState(false)
  const [userModules, setUserModules] = useState<string[]>([])
  const [savingModules, setSavingModules] = useState(false)

  // Email
  const [showEmailDialog, setShowEmailDialog] = useState(false)
  const [emailSubject, setEmailSubject] = useState('')
  const [emailMessage, setEmailMessage] = useState('')
  const [sendingEmail, setSendingEmail] = useState(false)

  // API keys
  const [showApiKeysDialog, setShowApiKeysDialog] = useState(false)
  const [apiKeys, setApiKeys] = useState<ApiKeyDetail[]>([])
  const [apiKeyMapping, setApiKeyMapping] = useState<Record<string, string>>({})
  const [apiKeysLoading, setApiKeysLoading] = useState(false)
  const [revokeTarget, setRevokeTarget] = useState<{ maskedKey: string; label: string | null } | null>(null)
  const [revokingKey, setRevokingKey] = useState(false)

  // Delete (single)
  const [deleteTarget, setDeleteTarget] = useState<AdminUser | null>(null)
  const [deleteConfirmText, setDeleteConfirmText] = useState('')
  const [deletingUser, setDeletingUser] = useState(false)

  // Reject
  const [rejectTarget, setRejectTarget] = useState<AdminUser | null>(null)
  const [rejectingUser, setRejectingUser] = useState(false)

  // Impersonate
  const [impersonateTarget, setImpersonateTarget] = useState<AdminUser | null>(null)
  const [impersonating, setImpersonating] = useState(false)

  // Bulk operations
  const [bulkActionLoading, setBulkActionLoading] = useState(false)
  const [bulkTierTargets, setBulkTierTargets] = useState<AdminUser[] | null>(null)
  const [bulkTierValue, setBulkTierValue] = useState('')
  const [bulkDeleteTargets, setBulkDeleteTargets] = useState<AdminUser[] | null>(null)

  useEffect(() => {
    loadAll()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const loadAll = async () => {
    try {
      setLoading(true)
      setError(null)
      const [usersData, orgsData, tiers] = await Promise.all([
        apiClient.getAllUsers(0, 1000),
        apiClient.getAllOrganizations(0, 1000).catch(() => []),
        apiClient.getTiersList().catch(() => ({})),
      ])
      setUsers((usersData as AdminUser[]) || [])
      setOrganizations(Array.isArray(orgsData) ? orgsData : (orgsData as any)?.organizations || [])
      setTiersList(tiers || {})
    } catch (e: any) {
      console.error('Failed to load users', e)
      setError(e?.message || 'Failed to load users')
    } finally {
      setLoading(false)
    }
  }

  const reloadUsers = async () => {
    try {
      const usersData = await apiClient.getAllUsers(0, 1000)
      setUsers((usersData as AdminUser[]) || [])
    } catch (e: any) {
      console.error('Failed to reload users', e)
      toast({ title: 'Failed to refresh user list', description: e?.message, variant: 'destructive' })
    }
  }

  const loadAvailableModules = async () => {
    try {
      const result = await apiClient.getAvailableModules()
      setAvailableModules((result as any).modules || [])
    } catch (e) {
      console.error('Failed to load available modules', e)
    }
  }

  const tierKeys = useMemo(() => {
    const keys = Object.keys(tiersList)
    return keys.length ? keys : FALLBACK_TIER_KEYS
  }, [tiersList])

  const filteredUsers = useMemo(() => {
    const term = searchTerm.trim().toLowerCase()
    const matchesStatus = (u: AdminUser) => {
      switch (statusFilter) {
        case 'pending':
          return u.admin_approved === false
        case 'stale_approved':
          return u.admin_approved === true && !u.last_login
        case 'active':
          return u.is_active && u.admin_approved !== false
        case 'all':
        default:
          return true
      }
    }

    const result = users.filter((u) => {
      if (term && !(u.email.toLowerCase().includes(term) || `${u.first_name} ${u.last_name}`.toLowerCase().includes(term))) {
        return false
      }
      return matchesStatus(u)
    })

    return [...result].sort((a, b) => {
      switch (sortBy) {
        case 'oldest':
          return new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
        case 'last_activity': {
          if (a.last_login && b.last_login) return new Date(b.last_login).getTime() - new Date(a.last_login).getTime()
          if (a.last_login) return -1
          if (b.last_login) return 1
          return 0
        }
        case 'newest':
        default:
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      }
    })
  }, [users, searchTerm, statusFilter, sortBy])

  // --- Create / Invite ----------------------------------------------------

  const handleCreateUser = async () => {
    if (!newUser.email || !newUser.password) {
      toast({ title: 'Email and password are required', variant: 'destructive' })
      return
    }
    setCreatingUser(true)
    try {
      await apiClient.adminCreateUser({
        ...newUser,
        request_limit: tiersList[newUser.subscription_tier]?.request_limit ?? 5,
        organization_id: newUser.organization_id || undefined,
      })
      setShowCreateDialog(false)
      setNewUser({ email: '', password: '', first_name: '', last_name: '', role: 'user', subscription_tier: 'freemium', organization_id: '' })
      await reloadUsers()
      toast({ title: 'User created successfully' })
    } catch (e: any) {
      toast({ title: 'Failed to create user', description: e?.message, variant: 'destructive' })
    } finally {
      setCreatingUser(false)
    }
  }

  const handleInviteUser = async () => {
    if (!inviteUser.email) {
      toast({ title: 'Email is required', variant: 'destructive' })
      return
    }
    setInvitingUser(true)
    try {
      await apiClient.adminInviteUser({
        ...inviteUser,
        request_limit: tiersList[inviteUser.subscription_tier]?.request_limit ?? 5,
      })
      setShowInviteDialog(false)
      setInviteUser({ email: '', first_name: '', last_name: '', role: 'user', subscription_tier: 'freemium' })
      await reloadUsers()
      toast({ title: 'Invitation sent successfully' })
    } catch (e: any) {
      toast({ title: 'Failed to send invitation', description: e?.message, variant: 'destructive' })
    } finally {
      setInvitingUser(false)
    }
  }

  // --- Edit ----------------------------------------------------------------

  const handleUpdateUser = async () => {
    if (!selectedUser) return
    setSavingEdit(true)
    try {
      await apiClient.adminUpdateUser(selectedUser.id, editUser)
      setShowEditDialog(false)
      setSelectedUser(null)
      setEditUser({})
      await reloadUsers()
      toast({ title: 'User updated' })
    } catch (e: any) {
      toast({ title: 'Failed to update user', description: e?.message, variant: 'destructive' })
    } finally {
      setSavingEdit(false)
    }
  }

  // --- Activate / Deactivate ------------------------------------------------

  const handleActivate = async (u: AdminUser) => {
    try {
      await apiClient.adminActivateUser(u.id)
      await reloadUsers()
      toast({ title: 'User activated' })
    } catch (e: any) {
      toast({ title: 'Failed to activate user', description: e?.message, variant: 'destructive' })
    }
  }

  const handleDeactivate = async (u: AdminUser) => {
    try {
      await apiClient.adminDeactivateUser(u.id)
      await reloadUsers()
      toast({ title: 'User deactivated' })
    } catch (e: any) {
      toast({ title: 'Failed to deactivate user', description: e?.message, variant: 'destructive' })
    }
  }

  // --- Credits --------------------------------------------------------------

  const handleAssignCredits = async () => {
    setAssigningCredits(true)
    try {
      await apiClient.adminAssignCredits(creditData)
      setShowCreditDialog(false)
      setCreditData({ user_id: '', credits: 0, reason: '' })
      await reloadUsers()
      toast({ title: 'Credits assigned' })
    } catch (e: any) {
      toast({ title: 'Failed to assign credits', description: e?.message, variant: 'destructive' })
    } finally {
      setAssigningCredits(false)
    }
  }

  const handleResetCredits = async () => {
    if (!resetCreditsTarget) return
    try {
      await apiClient.adminResetCredits({ user_id: resetCreditsTarget.id })
      await reloadUsers()
      toast({ title: 'Credit usage reset' })
    } catch (e: any) {
      toast({ title: 'Failed to reset credits', description: e?.message, variant: 'destructive' })
    } finally {
      setResetCreditsTarget(null)
    }
  }

  // --- Logs -------------------------------------------------------------------

  const handleViewLogs = async (u: AdminUser) => {
    setShowLogsDialog(true)
    setLogsLoading(true)
    try {
      const logs = await apiClient.getUserQueryHistory(u.id, { limit: 50 })
      setQueryLogs((logs as any).queries || [])
    } catch (e: any) {
      toast({ title: 'Failed to load logs', description: e?.message, variant: 'destructive' })
    } finally {
      setLogsLoading(false)
    }
  }

  // --- Permissions --------------------------------------------------------------

  const handleUpdatePermissions = async () => {
    if (!selectedUser) return
    setSavingPermissions(true)
    try {
      await apiClient.adminUpdateUserPermissions(selectedUser.id, userPermissions)
      setShowPermissionsDialog(false)
      setSelectedUser(null)
      setUserPermissions([])
      await reloadUsers()
      toast({ title: 'Permissions updated' })
    } catch (e: any) {
      toast({ title: 'Failed to update permissions', description: e?.message, variant: 'destructive' })
    } finally {
      setSavingPermissions(false)
    }
  }

  // --- Module access --------------------------------------------------------------

  const handleUpdateModules = async () => {
    if (!selectedUser) return
    setSavingModules(true)
    try {
      await apiClient.updateUserModules(selectedUser.id, userModules)
      setShowModulesDialog(false)
      setSelectedUser(null)
      setUserModules([])
      await reloadUsers()
      toast({ title: 'Module access updated' })
    } catch (e: any) {
      toast({ title: 'Failed to update module access', description: e?.message, variant: 'destructive' })
    } finally {
      setSavingModules(false)
    }
  }

  // --- Email / password reset --------------------------------------------------------------

  const handleSendEmail = async () => {
    if (!selectedUser) return
    if (!emailMessage.trim()) {
      toast({ title: 'Message is required', variant: 'destructive' })
      return
    }
    setSendingEmail(true)
    try {
      await apiClient.adminSendEmail(selectedUser.id, emailSubject || 'Message from Sycek OSINT', emailMessage)
      toast({ title: 'Email sent' })
      setShowEmailDialog(false)
      setEmailSubject('')
      setEmailMessage('')
    } catch (e: any) {
      toast({ title: 'Failed to send email', description: e?.message, variant: 'destructive' })
    } finally {
      setSendingEmail(false)
    }
  }

  const handleSendPasswordReset = async (u: AdminUser) => {
    try {
      await apiClient.adminSendPasswordReset(u.id)
      toast({ title: `Password reset sent to ${u.email}` })
    } catch (e: any) {
      toast({ title: 'Failed to send password reset', description: e?.message, variant: 'destructive' })
    }
  }

  // --- Approve / reject --------------------------------------------------------------

  const handleApprove = async (u: AdminUser) => {
    try {
      await apiClient.adminApproveUser(u.id)
      await reloadUsers()
      toast({ title: 'User approved' })
    } catch (e: any) {
      toast({ title: 'Failed to approve', description: e?.message, variant: 'destructive' })
    }
  }

  const handleSendApprovalEmail = async (u: AdminUser) => {
    try {
      await apiClient.adminSendApprovalEmail(u.id)
      toast({ title: 'Approval email sent' })
    } catch (e: any) {
      toast({ title: 'Failed to send email', description: e?.message, variant: 'destructive' })
    }
  }

  const handleReject = async () => {
    if (!rejectTarget) return
    setRejectingUser(true)
    try {
      await apiClient.adminRejectUser(rejectTarget.id)
      await reloadUsers()
      toast({ title: 'User rejected' })
    } catch (e: any) {
      toast({ title: 'Failed to reject', description: e?.message, variant: 'destructive' })
    } finally {
      setRejectingUser(false)
      setRejectTarget(null)
    }
  }

  // --- Delete --------------------------------------------------------------

  const handleDeleteUser = async () => {
    if (!deleteTarget) return
    setDeletingUser(true)
    try {
      await apiClient.adminDeleteUser(deleteTarget.id)
      await reloadUsers()
      toast({ title: 'User deleted' })
    } catch (e: any) {
      toast({ title: 'Failed to delete user', description: e?.message, variant: 'destructive' })
    } finally {
      setDeletingUser(false)
      setDeleteTarget(null)
      setDeleteConfirmText('')
    }
  }

  // --- API keys --------------------------------------------------------------

  const openApiKeysDialog = async (u: AdminUser) => {
    setSelectedUser(u)
    setShowApiKeysDialog(true)
    setApiKeysLoading(true)
    try {
      const res = await apiClient.adminGetUserApiKeys(u.id)
      setApiKeys(res.keys_detail || [])
      setApiKeyMapping(res._key_mapping || {})
    } catch (e: any) {
      toast({ title: 'Failed to load API keys', description: e?.message, variant: 'destructive' })
    } finally {
      setApiKeysLoading(false)
    }
  }

  const handleRevokeApiKey = async () => {
    if (!selectedUser || !revokeTarget) return
    const apiKey = apiKeyMapping[revokeTarget.maskedKey]
    if (!apiKey) {
      toast({ title: 'Could not resolve API key', variant: 'destructive' })
      setRevokeTarget(null)
      return
    }
    setRevokingKey(true)
    try {
      await apiClient.adminRevokeUserApiKey(selectedUser.id, apiKey)
      setApiKeys((prev) => prev.filter((k) => k.masked_key !== revokeTarget.maskedKey))
      toast({ title: 'API key revoked' })
    } catch (e: any) {
      toast({ title: 'Failed to revoke API key', description: e?.message, variant: 'destructive' })
    } finally {
      setRevokingKey(false)
      setRevokeTarget(null)
    }
  }

  // --- Impersonation --------------------------------------------------------------

  const handleImpersonate = async () => {
    if (!impersonateTarget) return
    setImpersonating(true)
    try {
      const res = await apiClient.adminImpersonateUser(impersonateTarget.id)
      await startImpersonation(res.access_token, res.expires_in, res.impersonated_user.email, res.impersonated_user.id)
      navigate('/app/dashboard')
    } catch (e: any) {
      toast({ title: 'Failed to impersonate user', description: e?.message, variant: 'destructive' })
    } finally {
      setImpersonating(false)
      setImpersonateTarget(null)
    }
  }

  // --- Bulk operations --------------------------------------------------------------

  const summarizeBulkResult = (result: { updated: string[]; failed: { user_id: string; error: string }[] }) => {
    const parts = [`${result.updated.length} succeeded`]
    if (result.failed.length) parts.push(`${result.failed.length} failed`)
    toast({
      title: parts.join(', '),
      description: result.failed.length ? result.failed.map((f) => `${f.user_id}: ${f.error}`).join('; ') : undefined,
      variant: result.failed.length && !result.updated.length ? 'destructive' : undefined,
    })
  }

  const runBulkOperation = async (
    userIds: string[],
    action: 'activate' | 'deactivate' | 'tier_change' | 'delete' | 'approve' | 'reject',
    subscriptionTier?: string
  ) => {
    setBulkActionLoading(true)
    try {
      const result = await apiClient.adminBulkUserOperation({ user_ids: userIds, action, subscription_tier: subscriptionTier })
      summarizeBulkResult(result)
      await reloadUsers()
    } catch (e: any) {
      toast({ title: 'Bulk operation failed', description: e?.message, variant: 'destructive' })
    } finally {
      setBulkActionLoading(false)
    }
  }

  // --- Table config --------------------------------------------------------------

  const columns: DataTableColumn<AdminUser>[] = [
    { key: 'email', header: 'Email', render: (u) => u.email },
    { key: 'name', header: 'Name', render: (u) => `${u.first_name} ${u.last_name}` },
    {
      key: 'role',
      header: 'Role',
      render: (u) => <Badge variant={u.role === 'admin' ? 'default' : 'outline'}>{u.role}</Badge>,
    },
    {
      key: 'tier',
      header: 'Tier',
      render: (u) => <Badge variant="outline">{u.subscription_tier}</Badge>,
    },
    {
      key: 'credits',
      header: 'Credits Used',
      render: (u) => (
        <span className="text-sm" title="Paid credits consumed / limit. Credits & Usage section shows total API calls.">
          {u.requests_used} / {u.request_limit === -1 ? '∞' : u.request_limit}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (u) => (
        <div className="flex flex-wrap gap-1">
          <Badge variant={u.is_active ? 'default' : 'secondary'}>{u.is_active ? 'Active' : 'Inactive'}</Badge>
          {u.email_verified === false && (
            <Badge variant="outline" className="text-yellow-500 border-yellow-500">Unverified</Badge>
          )}
          {u.role !== 'admin' && u.admin_approved === false && (
            <Badge variant="outline" className="text-amber-500 border-amber-500">Pending Approval</Badge>
          )}
        </div>
      ),
    },
    {
      key: 'registration',
      header: 'Registration',
      render: (u) => (
        <div className="text-xs">
          <div>{u.created_at ? new Date(u.created_at).toLocaleDateString() : 'N/A'}</div>
          {u.registration_ip && <div className="text-muted-foreground">IP: {u.registration_ip}</div>}
        </div>
      ),
    },
    {
      key: 'invite',
      header: 'Invite/Activation',
      render: (u) => (
        <div className="text-xs space-y-0.5">
          {u.invite_sent_at ? (
            <div className="text-green-500">Invited: {new Date(u.invite_sent_at).toLocaleDateString()}</div>
          ) : (
            <div className="text-muted-foreground">No invite</div>
          )}
          {u.account_activated_at ? (
            <div className="text-green-500">Activated: {new Date(u.account_activated_at).toLocaleDateString()}</div>
          ) : (
            <div className="text-yellow-500">Not activated</div>
          )}
        </div>
      ),
    },
    {
      key: 'last_login',
      header: 'Last Login',
      render: (u) =>
        u.last_login ? (
          <span className="text-xs">{new Date(u.last_login).toLocaleDateString()}</span>
        ) : (
          <span className="text-xs text-amber-500 font-medium">Never logged in</span>
        ),
    },
  ]

  const rowActions: DataTableAction<AdminUser>[] = [
    {
      label: 'Edit User',
      icon: <Edit className="mr-2 h-4 w-4" />,
      onClick: (u) => {
        setSelectedUser(u)
        setEditUser(u)
        setShowEditDialog(true)
      },
    },
    {
      label: 'Assign Credits',
      icon: <DollarSign className="mr-2 h-4 w-4" />,
      onClick: (u) => {
        setCreditData({ user_id: u.id, credits: 0, reason: '' })
        setShowCreditDialog(true)
      },
    },
    {
      label: 'Reset Credit Usage',
      icon: <RefreshCw className="mr-2 h-4 w-4" />,
      onClick: (u) => setResetCreditsTarget(u),
    },
    {
      label: 'View Query Logs',
      icon: <Eye className="mr-2 h-4 w-4" />,
      onClick: (u) => handleViewLogs(u),
    },
    {
      label: 'Manage Permissions',
      icon: <Lock className="mr-2 h-4 w-4" />,
      onClick: (u) => {
        setSelectedUser(u)
        setUserPermissions(u.permissions || [])
        setShowPermissionsDialog(true)
      },
    },
    {
      label: 'Module Access',
      icon: <Settings className="mr-2 h-4 w-4" />,
      onClick: (u) => {
        setSelectedUser(u)
        setUserModules(u.modules || [])
        setShowModulesDialog(true)
        void loadAvailableModules()
      },
    },
    {
      label: 'Send Email',
      icon: <Mail className="mr-2 h-4 w-4" />,
      onClick: (u) => {
        setSelectedUser(u)
        setShowEmailDialog(true)
      },
    },
    {
      label: 'Send Password Reset',
      icon: <Key className="mr-2 h-4 w-4" />,
      onClick: (u) => handleSendPasswordReset(u),
    },
    {
      label: 'API Keys',
      icon: <KeyRound className="mr-2 h-4 w-4" />,
      onClick: (u) => openApiKeysDialog(u),
    },
    {
      label: 'View as',
      icon: <LogIn className="mr-2 h-4 w-4" />,
      hidden: (u) => u.role === 'admin' || u.id === currentUser?.id,
      onClick: (u) => setImpersonateTarget(u),
    },
    {
      label: 'Approve User',
      icon: <CheckCircle className="mr-2 h-4 w-4" />,
      hidden: (u) => u.role === 'admin' || u.admin_approved !== false,
      onClick: (u) => handleApprove(u),
    },
    {
      label: 'Send Approval Email',
      icon: <Send className="mr-2 h-4 w-4" />,
      hidden: (u) => u.role === 'admin' || u.admin_approved !== false,
      onClick: (u) => handleSendApprovalEmail(u),
    },
    {
      label: 'Reject User',
      icon: <XCircle className="mr-2 h-4 w-4" />,
      hidden: (u) => u.role === 'admin' || u.admin_approved !== true,
      onClick: (u) => setRejectTarget(u),
    },
    {
      label: 'Activate',
      icon: <Power className="mr-2 h-4 w-4" />,
      hidden: (u) => u.is_active,
      onClick: (u) => handleActivate(u),
    },
    {
      label: 'Deactivate',
      icon: <PowerOff className="mr-2 h-4 w-4" />,
      hidden: (u) => !u.is_active,
      onClick: (u) => handleDeactivate(u),
    },
    {
      label: 'Delete User',
      icon: <Trash2 className="mr-2 h-4 w-4" />,
      destructive: true,
      hidden: (u) => u.id === currentUser?.id,
      onClick: (u) => setDeleteTarget(u),
    },
  ]

  const bulkActions: DataTableBulkAction<AdminUser>[] = [
    {
      label: 'Approve',
      icon: <CheckCircle className="mr-2 h-4 w-4" />,
      onClick: (selected) => runBulkOperation(selected.map((u) => u.id), 'approve'),
    },
    {
      label: 'Reject',
      icon: <XCircle className="mr-2 h-4 w-4" />,
      destructive: true,
      onClick: (selected) => runBulkOperation(selected.map((u) => u.id), 'reject'),
    },
    {
      label: 'Activate',
      icon: <Power className="mr-2 h-4 w-4" />,
      onClick: (selected) => runBulkOperation(selected.map((u) => u.id), 'activate'),
    },
    {
      label: 'Deactivate',
      icon: <PowerOff className="mr-2 h-4 w-4" />,
      onClick: (selected) => runBulkOperation(selected.map((u) => u.id), 'deactivate'),
    },
    {
      label: 'Change Tier',
      icon: <RefreshCw className="mr-2 h-4 w-4" />,
      onClick: (selected) => {
        setBulkTierTargets(selected)
        setBulkTierValue(tierKeys[0] || 'freemium')
      },
    },
    {
      label: 'Delete',
      destructive: true,
      icon: <Trash2 className="mr-2 h-4 w-4" />,
      onClick: (selected) => setBulkDeleteTargets(selected),
    },
  ]

  const csvColumns: CsvColumn<AdminUser>[] = [
    { header: 'Email', value: (u) => u.email },
    { header: 'Name', value: (u) => `${u.first_name} ${u.last_name}` },
    { header: 'Role', value: (u) => u.role },
    { header: 'Tier', value: (u) => u.subscription_tier },
    { header: 'Credits Used', value: (u) => u.requests_used },
    { header: 'Limit', value: (u) => (u.request_limit === -1 ? 'Unlimited' : u.request_limit) },
    { header: 'Status', value: (u) => (u.is_active ? 'Active' : 'Inactive') },
    { header: 'Registered', value: (u) => (u.created_at ? new Date(u.created_at).toLocaleDateString() : '') },
  ]

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <CardTitle>
            User Management
            <span className="ml-2 text-sm font-normal text-muted-foreground">{filteredUsers.length} users</span>
          </CardTitle>
          <div className="flex flex-wrap gap-2">
            <Input
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-64"
            />
            <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as typeof statusFilter)}>
              <SelectTrigger className="w-52"><SelectValue placeholder="Filter" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="pending">Pending Approval</SelectItem>
                <SelectItem value="stale_approved">Approved, Never Logged In</SelectItem>
                <SelectItem value="active">Active</SelectItem>
              </SelectContent>
            </Select>
            <Select value={sortBy} onValueChange={(v) => setSortBy(v as typeof sortBy)}>
              <SelectTrigger className="w-44"><SelectValue placeholder="Sort" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="newest">Newest signup</SelectItem>
                <SelectItem value="oldest">Oldest signup</SelectItem>
                <SelectItem value="last_activity">Last activity</SelectItem>
              </SelectContent>
            </Select>
            <ExportCsvButton rows={filteredUsers} columns={csvColumns} filenamePrefix="users" label="Export" />

            <Dialog open={showInviteDialog} onOpenChange={setShowInviteDialog}>
              <DialogTrigger asChild>
                <Button variant="outline">
                  <Mail className="w-4 h-4 mr-2" />
                  Invite User
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>Invite User via Email</DialogTitle>
                  <p className="text-sm text-muted-foreground mt-1">
                    Send an invitation email to create a new user account
                  </p>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div>
                    <Label>Email *</Label>
                    <Input
                      type="email"
                      value={inviteUser.email}
                      onChange={(e) => setInviteUser({ ...inviteUser, email: e.target.value })}
                      placeholder="user@example.com"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label>First Name</Label>
                      <Input value={inviteUser.first_name} onChange={(e) => setInviteUser({ ...inviteUser, first_name: e.target.value })} />
                    </div>
                    <div>
                      <Label>Last Name</Label>
                      <Input value={inviteUser.last_name} onChange={(e) => setInviteUser({ ...inviteUser, last_name: e.target.value })} />
                    </div>
                  </div>
                  <div>
                    <Label>Role</Label>
                    <Select value={inviteUser.role} onValueChange={(v) => setInviteUser({ ...inviteUser, role: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="user">User</SelectItem>
                        <SelectItem value="admin">Admin</SelectItem>
                        <SelectItem value="org_admin">Org Admin</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Subscription Tier</Label>
                    <Select value={inviteUser.subscription_tier} onValueChange={(v) => setInviteUser({ ...inviteUser, subscription_tier: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {tierKeys.map((tier) => (
                          <SelectItem key={tier} value={tier}>{tierLabel(tier, tiersList)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button onClick={handleInviteUser} className="w-full" disabled={!inviteUser.email || invitingUser}>
                    {invitingUser ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}
                    Send Invitation
                  </Button>
                </div>
              </DialogContent>
            </Dialog>

            <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
              <DialogTrigger asChild>
                <Button>
                  <UserPlus className="w-4 h-4 mr-2" />
                  Create User
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>Create New User</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div>
                    <Label>Email</Label>
                    <Input value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} />
                  </div>
                  <div>
                    <Label>Password</Label>
                    <Input type="password" value={newUser.password} onChange={(e) => setNewUser({ ...newUser, password: e.target.value })} />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label>First Name</Label>
                      <Input value={newUser.first_name} onChange={(e) => setNewUser({ ...newUser, first_name: e.target.value })} />
                    </div>
                    <div>
                      <Label>Last Name</Label>
                      <Input value={newUser.last_name} onChange={(e) => setNewUser({ ...newUser, last_name: e.target.value })} />
                    </div>
                  </div>
                  <div>
                    <Label>Role</Label>
                    <Select value={newUser.role} onValueChange={(v) => setNewUser({ ...newUser, role: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="user">User</SelectItem>
                        <SelectItem value="admin">Admin</SelectItem>
                        <SelectItem value="org_admin">Org Admin</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Subscription Tier</Label>
                    <Select value={newUser.subscription_tier} onValueChange={(v) => setNewUser({ ...newUser, subscription_tier: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {tierKeys.map((tier) => (
                          <SelectItem key={tier} value={tier}>{tierLabel(tier, tiersList)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Organization (optional)</Label>
                    <Select
                      value={newUser.organization_id || 'none'}
                      onValueChange={(v) => setNewUser({ ...newUser, organization_id: v === 'none' ? '' : v })}
                    >
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No organization</SelectItem>
                        {organizations.map((org) => (
                          <SelectItem key={org.id} value={org.id}>{org.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button onClick={handleCreateUser} className="w-full" disabled={creatingUser || !newUser.email || !newUser.password}>
                    {creatingUser ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <UserPlus className="w-4 h-4 mr-2" />}
                    Create User
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
        <CardContent>
          {error && <p className="text-sm text-destructive mb-4">{error}</p>}
          <DataTable
            columns={columns}
            rows={filteredUsers}
            getRowId={(u) => u.id}
            rowActions={rowActions}
            bulkActions={bulkActions}
            isLoading={loading}
            emptyMessage="No users found."
          />
          {bulkActionLoading && (
            <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1">
              <Loader2 className="h-3 w-3 animate-spin" /> Applying bulk operation...
            </p>
          )}
        </CardContent>
      </Card>

      {/* Edit User Dialog */}
      <Dialog
        open={showEditDialog}
        onOpenChange={(open) => {
          setShowEditDialog(open)
          if (!open) {
            setSelectedUser(null)
            setEditUser({})
          }
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
          </DialogHeader>
          {selectedUser && (
            <div className="space-y-4 mt-4">
              <div>
                <Label>Email</Label>
                <Input value={editUser.email ?? selectedUser.email} onChange={(e) => setEditUser({ ...editUser, email: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label>First Name</Label>
                  <Input value={editUser.first_name ?? selectedUser.first_name} onChange={(e) => setEditUser({ ...editUser, first_name: e.target.value })} />
                </div>
                <div>
                  <Label>Last Name</Label>
                  <Input value={editUser.last_name ?? selectedUser.last_name} onChange={(e) => setEditUser({ ...editUser, last_name: e.target.value })} />
                </div>
              </div>
              <div>
                <Label>Role</Label>
                <Select value={editUser.role ?? selectedUser.role} onValueChange={(v) => setEditUser({ ...editUser, role: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="user">User</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="org_admin">Org Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Subscription Tier</Label>
                <Select
                  value={editUser.subscription_tier ?? selectedUser.subscription_tier}
                  onValueChange={(v) => setEditUser({ ...editUser, subscription_tier: v })}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {tierKeys.map((tier) => (
                      <SelectItem key={tier} value={tier}>{tierLabel(tier, tiersList)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Organization</Label>
                <Select
                  value={editUser.organization_id ?? selectedUser.organization_id ?? 'none'}
                  onValueChange={(v) => setEditUser({ ...editUser, organization_id: v === 'none' ? '' : v })}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No organization</SelectItem>
                    {organizations.map((org) => (
                      <SelectItem key={org.id} value={org.id}>{org.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-1">Moving a user to "No organization" clears their org membership</p>
              </div>
              <div>
                <Label>Request Limit</Label>
                <Input
                  type="number"
                  value={editUser.request_limit ?? selectedUser.request_limit}
                  onChange={(e) => setEditUser({ ...editUser, request_limit: parseInt(e.target.value, 10) })}
                />
              </div>
              <div>
                <Label>Requests Used</Label>
                <Input
                  type="number"
                  value={editUser.requests_used ?? selectedUser.requests_used}
                  onChange={(e) => setEditUser({ ...editUser, requests_used: parseInt(e.target.value, 10) })}
                />
              </div>
              <Button onClick={handleUpdateUser} className="w-full" disabled={savingEdit}>
                {savingEdit && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Update User
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Assign Credits Dialog */}
      <Dialog
        open={showCreditDialog}
        onOpenChange={(open) => {
          setShowCreditDialog(open)
          if (!open) setCreditData({ user_id: '', credits: 0, reason: '' })
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign Credits</DialogTitle>
            {creditData.user_id && (
              <p className="text-sm text-muted-foreground mt-1">
                Assigning to: {users.find((u) => u.id === creditData.user_id)?.email || creditData.user_id}
              </p>
            )}
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>Credits (can be negative to deduct)</Label>
              <Input
                type="number"
                value={creditData.credits || ''}
                onChange={(e) => setCreditData({ ...creditData, credits: parseInt(e.target.value, 10) || 0 })}
              />
            </div>
            <div>
              <Label>Reason (optional)</Label>
              <Input value={creditData.reason} onChange={(e) => setCreditData({ ...creditData, reason: e.target.value })} />
            </div>
            <Button onClick={handleAssignCredits} className="w-full" disabled={!creditData.user_id || assigningCredits}>
              {assigningCredits && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Assign Credits
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* View Logs Dialog */}
      <Dialog open={showLogsDialog} onOpenChange={setShowLogsDialog}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>User Query Logs</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 mt-4">
            {logsLoading ? (
              <div className="flex items-center justify-center gap-2 py-8 text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading...
              </div>
            ) : queryLogs.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">No query logs found for this user.</p>
            ) : (
              queryLogs.map((log, idx) => (
                <div key={idx} className="p-3 border rounded">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <p className="font-mono text-sm font-semibold">{log.endpoint}</p>
                      <p className="text-xs text-muted-foreground">{new Date(log.timestamp).toLocaleString()}</p>
                      {log.query && <p className="text-xs text-muted-foreground mt-1">{log.query}</p>}
                      {log.error_message && <p className="text-xs text-destructive mt-1">{log.error_message}</p>}
                    </div>
                    <Badge variant={log.status === 'success' ? 'default' : 'destructive'}>{log.status}</Badge>
                  </div>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Permissions Dialog */}
      <Dialog
        open={showPermissionsDialog}
        onOpenChange={(open) => {
          setShowPermissionsDialog(open)
          if (!open) {
            setSelectedUser(null)
            setUserPermissions([])
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Manage Permissions</DialogTitle>
            <p className="text-sm text-muted-foreground mt-1">{selectedUser && `User: ${selectedUser.email}`}</p>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>Available Permissions</Label>
              <div className="grid grid-cols-2 gap-2 mt-2">
                {AVAILABLE_PERMISSIONS.map((perm) => (
                  <label key={perm} className="flex items-center space-x-2 p-2 border rounded cursor-pointer hover:bg-accent">
                    <Checkbox
                      checked={userPermissions.includes(perm)}
                      onCheckedChange={(checked) => {
                        if (checked === true) setUserPermissions([...userPermissions, perm])
                        else setUserPermissions(userPermissions.filter((p) => p !== perm))
                      }}
                    />
                    <span className="text-sm capitalize">{perm.replace('_', ' ')}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="flex gap-2">
              <Button onClick={handleUpdatePermissions} className="flex-1" disabled={savingPermissions}>
                {savingPermissions && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Update Permissions
              </Button>
              <Button variant="outline" onClick={() => setShowPermissionsDialog(false)}>Cancel</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Send Email Dialog */}
      <Dialog
        open={showEmailDialog}
        onOpenChange={(open) => {
          setShowEmailDialog(open)
          if (!open) {
            setSelectedUser(null)
            setEmailSubject('')
            setEmailMessage('')
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send Email to User</DialogTitle>
            <p className="text-sm text-muted-foreground mt-1">{selectedUser && `Sending to: ${selectedUser.email}`}</p>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>Subject</Label>
              <Input value={emailSubject} onChange={(e) => setEmailSubject(e.target.value)} placeholder="Email subject" />
            </div>
            <div>
              <Label>Message</Label>
              <Textarea
                className="min-h-[200px]"
                value={emailMessage}
                onChange={(e) => setEmailMessage(e.target.value)}
                placeholder="Enter your message here..."
              />
            </div>
            <div className="flex gap-2">
              <Button onClick={handleSendEmail} className="flex-1" disabled={sendingEmail}>
                {sendingEmail ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}
                Send Email
              </Button>
              <Button variant="outline" onClick={() => setShowEmailDialog(false)}>Cancel</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Module Access Dialog */}
      <Dialog
        open={showModulesDialog}
        onOpenChange={(open) => {
          setShowModulesDialog(open)
          if (!open) {
            setSelectedUser(null)
            setUserModules([])
          }
        }}
      >
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Manage Module Access</DialogTitle>
            <p className="text-sm text-muted-foreground mt-1">
              {selectedUser && `User: ${selectedUser.email} (${selectedUser.subscription_tier})`}
            </p>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>Available Modules</Label>
              <p className="text-xs text-muted-foreground mb-3">
                Select which modules this user can access. Use "Reset to Tier Defaults" to restore the defaults for {selectedUser?.subscription_tier || 'freemium'}.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {availableModules.map((module) => (
                  <label
                    key={module.id}
                    className={`flex items-start space-x-3 p-3 border rounded cursor-pointer transition-colors ${
                      userModules.includes(module.id) ? 'bg-primary/10 border-primary' : 'hover:bg-accent border-border'
                    }`}
                  >
                    <Checkbox
                      className="mt-1"
                      checked={userModules.includes(module.id)}
                      onCheckedChange={(checked) => {
                        if (checked === true) setUserModules([...userModules, module.id])
                        else setUserModules(userModules.filter((m) => m !== module.id))
                      }}
                    />
                    <div className="flex-1">
                      <div className="font-semibold text-sm">{module.name}</div>
                      <div className="text-xs text-muted-foreground mt-1">{module.description}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
            <div className="flex gap-2 pt-4 border-t">
              <Button onClick={handleUpdateModules} className="flex-1" disabled={savingModules}>
                {savingModules && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Update Module Access
              </Button>
              <Button variant="outline" onClick={() => setShowModulesDialog(false)}>Cancel</Button>
              <Button
                variant="outline"
                onClick={() => {
                  const tier = selectedUser?.subscription_tier || 'freemium'
                  setUserModules(tiersList[tier]?.modules || tiersList['freemium']?.modules || [])
                }}
              >
                Reset to Tier Defaults
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* API Keys Dialog */}
      <Dialog
        open={showApiKeysDialog}
        onOpenChange={(open) => {
          setShowApiKeysDialog(open)
          if (!open) {
            setApiKeys([])
            setApiKeyMapping({})
            setSelectedUser(null)
          }
        }}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>API Keys</DialogTitle>
            <p className="text-sm text-muted-foreground mt-1">{selectedUser && `User: ${selectedUser.email}`}</p>
          </DialogHeader>
          <div className="space-y-2 mt-4">
            {apiKeysLoading ? (
              <div className="flex items-center justify-center gap-2 py-8 text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading...
              </div>
            ) : apiKeys.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">No API keys for this user.</p>
            ) : (
              apiKeys.map((k) => (
                <div key={k.masked_key} className="flex items-center justify-between p-3 border rounded gap-2">
                  <div className="min-w-0">
                    <p className="font-mono text-sm truncate">{k.masked_key}</p>
                    <p className="text-xs text-muted-foreground">{k.label || 'Unlabeled'}</p>
                    <p className="text-xs text-muted-foreground">
                      Created: {k.created_at ? new Date(k.created_at).toLocaleDateString() : 'Unknown'} · Last used:{' '}
                      {k.last_used ? new Date(k.last_used).toLocaleString() : 'Never used'}
                    </p>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-destructive hover:text-destructive shrink-0"
                    onClick={() => setRevokeTarget({ maskedKey: k.masked_key, label: k.label })}
                  >
                    Revoke
                  </Button>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk: Change Tier Dialog */}
      <Dialog open={!!bulkTierTargets} onOpenChange={(open) => !open && setBulkTierTargets(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Change tier for {bulkTierTargets?.length || 0} users</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>New Tier</Label>
              <Select value={bulkTierValue} onValueChange={setBulkTierValue}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {tierKeys.map((tier) => (
                    <SelectItem key={tier} value={tier}>{tierLabel(tier, tiersList)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button
              className="w-full"
              disabled={!bulkTierValue || bulkActionLoading}
              onClick={async () => {
                if (!bulkTierTargets) return
                await runBulkOperation(bulkTierTargets.map((u) => u.id), 'tier_change', bulkTierValue)
                setBulkTierTargets(null)
              }}
            >
              {bulkActionLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Apply
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reset Credits confirm */}
      <AlertDialog open={!!resetCreditsTarget} onOpenChange={(open) => !open && setResetCreditsTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reset credit usage?</AlertDialogTitle>
            <AlertDialogDescription>
              This resets {resetCreditsTarget?.email}'s credit usage counter to zero. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleResetCredits}>Reset</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reject confirm */}
      <AlertDialog open={!!rejectTarget} onOpenChange={(open) => !open && setRejectTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reject this user?</AlertDialogTitle>
            <AlertDialogDescription>
              {rejectTarget?.email} will lose access until approved again.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction destructive disabled={rejectingUser} onClick={handleReject}>
              {rejectingUser ? 'Rejecting...' : 'Reject User'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete single user confirm */}
      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={(open) => {
          if (!open) {
            setDeleteTarget(null)
            setDeleteConfirmText('')
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Permanently delete this user?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete {deleteTarget?.email}. This cannot be undone. Type DELETE to confirm.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Input
            value={deleteConfirmText}
            onChange={(e) => setDeleteConfirmText(e.target.value)}
            placeholder="Type DELETE to confirm"
            autoFocus
          />
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              destructive
              disabled={deleteConfirmText !== 'DELETE' || deletingUser}
              onClick={handleDeleteUser}
            >
              {deletingUser ? 'Deleting...' : 'Delete User'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk delete confirm */}
      <AlertDialog open={!!bulkDeleteTargets} onOpenChange={(open) => !open && setBulkDeleteTargets(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {bulkDeleteTargets?.length || 0} users?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete: {bulkDeleteTargets?.map((u) => u.email).join(', ')}. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              destructive
              disabled={bulkActionLoading}
              onClick={async () => {
                if (!bulkDeleteTargets) return
                await runBulkOperation(bulkDeleteTargets.map((u) => u.id), 'delete')
                setBulkDeleteTargets(null)
              }}
            >
              Delete Users
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Revoke API key confirm */}
      <AlertDialog open={!!revokeTarget} onOpenChange={(open) => !open && setRevokeTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke this API key?</AlertDialogTitle>
            <AlertDialogDescription>
              {revokeTarget?.label || revokeTarget?.maskedKey} will no longer be able to authenticate. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction destructive disabled={revokingKey} onClick={handleRevokeApiKey}>
              {revokingKey ? 'Revoking...' : 'Revoke'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Impersonate confirm */}
      <AlertDialog open={!!impersonateTarget} onOpenChange={(open) => !open && setImpersonateTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>View as {impersonateTarget?.email}?</AlertDialogTitle>
            <AlertDialogDescription>
              You will act as {impersonateTarget?.email} for 15 minutes. Actions taken during this session will be
              attributed to that user.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction disabled={impersonating} onClick={handleImpersonate}>
              {impersonating ? 'Starting...' : 'View as user'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
